import { Siren, CircleCheck, CircleAlert, CircleX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';

interface DeepHealth {
  services: Record<string, { ok: boolean; latencyMs?: number; note?: string }>;
}

// Gap-fix pass 2: the 60-day historical strip used to render fabricated values
// (Web app 99.99%, WhatsApp 97.2%, etc.) with no time-series backing store.
// Removed until a real uptime snapshot job ships.

const LIVE_SERVICE_LABELS: Record<string, string> = {
  mongo: 'MongoDB Atlas',
  openai: 'Nexie · OpenAI',
  msg91: 'MSG91 · SMS + WhatsApp',
  azureComm: 'Azure Comm · Email',
};

/**
 * /platform/health — live deep-health probe + historical 60-day strip.
 *   Live tile pulls from `/platform/health/deep` every 30s.
 *   Historical uptime strips remain static (a proper time-series backing store is TBD).
 */
export function PlatformHealthPage() {
  const query = useQuery({
    queryKey: ['platform', 'health', 'deep'],
    queryFn: () => apiGet<DeepHealth>('/platform/health/deep'),
    refetchInterval: 30_000,
  });

  const services = query.data?.services ?? {};
  const allOk = Object.values(services).every((s) => s.ok);
  const okCount = Object.values(services).filter((s) => s.ok).length;
  const totalCount = Object.keys(services).length;

  return (
    <div className="stack">
      <style>{`
        .up { display: flex; gap: 2px; height: 28px; align-items: stretch; }
        .up i { flex: 1; border-radius: 2px; background: rgb(var(--ink-success) / .45); }
        .up i.deg { background: rgb(var(--ink-warning)); } .up i.down { background: rgb(var(--ink-danger)); }
        .svc { display: grid; grid-template-columns: minmax(0,180px) minmax(0,1fr) 80px; gap: 16px; align-items: center; padding: 12px 20px; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        @media (max-width: 639px) { .svc { grid-template-columns: 1fr auto; } .svc .up { grid-column: 1 / -1; grid-row: 2; } }
        .spark { width: 100%; height: 64px; }
        .live-svc { display: grid; grid-template-columns: 22px minmax(0,1fr) 130px 90px; gap: 12px; padding: 12px 20px; align-items: center; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .live-svc:last-child { border-bottom: 0; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Platform health</h1>
          <p className="subhead">
            {query.isLoading ? 'Probing…' : allOk ? `All ${totalCount} services responding.` : `${totalCount - okCount} of ${totalCount} services degraded.`}
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/platform/incidents">
            <Siren size={14} />Declare incident
          </Link>
        </div>
      </div>

      {/* Only the "Services now" KPI is real — Uptime 99.96% / p95 1.4s / Nexie spend ₹0
          were fabricated without a metrics backing store. Removed until time-series lands. */}
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-1">
        <div className="card kpi">
          <span className="kpi-label">Services now</span>
          <span className="kpi-value">{query.isLoading ? '…' : `${okCount}/${totalCount}`}</span>
          <span className="kpi-meta">Live probe · refreshes every 30s</span>
        </div>
      </div>

      {/* Live deep-health */}
      <section className="card">
        <div className="card-head">
          <h2 className="h-section">Live status</h2>
          <span className="helper">Last checked {query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toLocaleTimeString('en-IN') : '—'}</span>
        </div>
        <AsyncBoundary kind="table" inCard loading={query.isLoading} error={query.error} empty={totalCount === 0} emptyTitle="No probes configured">
          {Object.entries(services).map(([key, s]) => {
            const Icon = s.ok ? CircleCheck : (s.note?.includes('missing') ? CircleAlert : CircleX);
            const cls = s.ok ? 'text-success' : (s.note?.includes('missing') ? 'text-warning' : 'text-danger');
            return (
              <div key={key} className="live-svc">
                <Icon size={16} className={cls} />
                <div>
                  <div className="cell-title">{LIVE_SERVICE_LABELS[key] ?? key}</div>
                  <div className="cell-sub">{s.note ?? (s.ok ? 'Responding' : 'Failed to respond')}</div>
                </div>
                <span className="cell-sub text-right">{s.latencyMs != null ? `${s.latencyMs} ms` : '—'}</span>
                <span className={`badge ${s.ok ? 'badge-success' : 'badge-warning'}`}>{s.ok ? 'OK' : 'Degraded'}</span>
              </div>
            );
          })}
        </AsyncBoundary>
      </section>

      <div className="card card-pad">
        <p className="cell-sub m-0">
          Historical uptime strips, request-rate spark, and past-incident timeline are surfaced
          on <Link className="link" to="/platform/incidents">Incidents</Link>. Time-series
          uptime tracking (per plan §13) lands with the Prometheus / metrics rollout.
        </p>
      </div>
    </div>
  );
}
